import React, { useState } from 'react';
import type { MenuInputOption } from '@saviynt/design-system';
import {
  Button,
  InputField,
  MenuInput,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';

import type { AddLicensePanelProps, LicenseFormData } from './type';

import './AddLicensePanel.css';

/**
 * Add License Panel component for adding new licenses to the inventory
 * @description Displays a side panel form for users to add new licenses with vendor, product, and cost information
 */
function AddLicensePanel({
  isOpen,
  onClose,
  onSave,
}: AddLicensePanelProps): React.ReactElement {
  const localize = useInvertedLocalize();
  const [formData, setFormData] = useState<LicenseFormData>({
    licenseName: '',
    licenseTier: '',
    fueAllocation: '',
    costPerFue: '',
    startDate: '',
    endDate: '',
    notes: '',
  });
  const [selectedTierOptions, setSelectedTierOptions] = useState<
    MenuInputOption[]
  >([]);

  const tierOptions: MenuInputOption[] = [
    { label: localize('Standard'), value: 'standard' },
    { label: localize('Premium'), value: 'premium' },
    { label: localize('Enterprise'), value: 'enterprise' },
  ];

  const setField =
    (field: keyof LicenseFormData) =>
    (value: string): void => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  const handleSave = (): void => {
    onSave?.(formData);
    handleClose();
  };

  const handleClose = (): void => {
    setFormData({
      licenseName: '',
      licenseTier: '',
      fueAllocation: '',
      costPerFue: '',
      startDate: '',
      endDate: '',
      notes: '',
    });
    setSelectedTierOptions([]);
    onClose();
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleClose}
      header={{
        title: localize('Add License'),
        description: localize(
          'Capture the minimum details needed to track a new SAP license entitlement'
        ),
      }}
      layout={
        <div className='addLicensePanel'>
          {/* Body */}
          <div className='addLicensePanelBody'>
            {/* License Name */}
            <div className='addLicensePanelRow'>
              <div className='addLicensePanelField'>
                <InputField
                  name='licenseName'
                  placeholder={localize('e.g. SAP RISE FUE 2026')}
                  value={formData.licenseName}
                  setValue={setField('licenseName')}
                  label={
                    <Typography kind='body2-bold'>
                      {localize('License Name')}
                    </Typography>
                  }
                />
              </div>
            </div>

            {/* License Tier */}
            <div className='addLicensePanelRow'>
              <div className='addLicensePanelField'>
                <MenuInput
                  options={tierOptions}
                  selectedOptions={selectedTierOptions}
                  setSelectedOptions={setSelectedTierOptions}
                  onChange={(opts: MenuInputOption[] | undefined) => {
                    setFormData((prev) => ({
                      ...prev,
                      licenseTier: opts?.[0]?.value ?? '',
                    }));
                  }}
                  inputLabel={
                    <Typography kind='body2-bold'>
                      {localize('License Tier')}
                    </Typography>
                  }
                  placeholder={localize('Select Tier')}
                />
              </div>
            </div>

            {/* FUE Allocation and Cost per FUE */}
            <div className='addLicensePanelRow addLicensePanelRowDouble'>
              <div className='addLicensePanelField'>
                <InputField
                  name='fueAllocation'
                  type='number'
                  placeholder='4200'
                  value={formData.fueAllocation}
                  setValue={setField('fueAllocation')}
                  label={
                    <Typography kind='body2-bold'>
                      {localize('FUE Allocation')}
                    </Typography>
                  }
                />
              </div>
              <div className='addLicensePanelField'>
                <InputField
                  name='costPerFue'
                  type='number'
                  placeholder='960'
                  value={formData.costPerFue}
                  setValue={setField('costPerFue')}
                  label={
                    <Typography kind='body2-bold'>
                      {localize('Cost per FUE ($)')}
                    </Typography>
                  }
                />
              </div>
            </div>

            {/* Start Date and End Date */}
            <div className='addLicensePanelRow addLicensePanelRowDouble'>
              <div className='addLicensePanelField'>
                <InputField
                  name='startDate'
                  type='date'
                  placeholder=''
                  value={formData.startDate}
                  setValue={setField('startDate')}
                  label={
                    <Typography kind='body2-bold'>
                      {localize('Start Date')}
                    </Typography>
                  }
                />
              </div>
              <div className='addLicensePanelField'>
                <InputField
                  name='endDate'
                  type='date'
                  placeholder=''
                  value={formData.endDate}
                  setValue={setField('endDate')}
                  label={
                    <Typography kind='body2-bold'>
                      {localize('End Date')}
                    </Typography>
                  }
                />
              </div>
            </div>

            {/* Notes */}
            <div className='addLicensePanelRow'>
              <div className='addLicensePanelField'>
                <InputField
                  name='notes'
                  kind='multiline'
                  placeholder={localize('Contract reference, owner, etc.')}
                  value={formData.notes}
                  setValue={setField('notes')}
                  label={
                    <Typography kind='body2-bold'>
                      {localize('Notes (optional)')}
                    </Typography>
                  }
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className='addLicensePanelFooter'>
            <Button
              kind='ghost'
              onClick={handleClose}
              className='addLicensePanelCancelBtn'>
              {localize('Cancel')}
            </Button>
            <Button
              kind='filled'
              onClick={handleSave}
              className='addLicensePanelSaveBtn'>
              {localize('Save License')}
            </Button>
          </div>
        </div>
      }
      className='addLicensePanelSidePanel'
    />
  );
}

export default AddLicensePanel;
